import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Commands, Results } from '@local/client-contracts';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { isRightClick } from '@shared/utils';
import { keyCodes, KeyName } from '@local/ts-infra';
import { PopupRef } from '@local/ui-infra';
import { HubService } from 'src/app/bar/services/hub.service';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { stopEvent } from '@shared/utils/elements-util';

@Component({
  selector: 'result-snippet',
  templateUrl: './result-snippet.component.html',
  styleUrls: ['./result-snippet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultSnippetComponent {
  private keyHandlerId: string;
  private selectionSnippet = '';
  private isSameSelection: boolean;
  activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;

  @Input() model: Results.Snippet;
  @Input() highlights: Array<string>;

  @Output() invoke = new EventEmitter<Commands.Command>();

  constructor(private contextMenuService: ContextMenuService, private hubService: HubService, private keyboardService: KeyboardService) {
    this.registerKeyboardHandler();
  }

  ngOnDestroy(): void {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
      this.keyHandlerId = null;
    }
  }

  mouseup(event: MouseEvent) {
    const prevSelectionSnippet = this.selectionSnippet;
    this.selectionSnippet = window.getSelection().toString();
    if (!this.selectionSnippet) return;
    stopEvent(event);
    if (prevSelectionSnippet !== this.selectionSnippet) {
      this.isSameSelection = true;
    }
  }

  mousedown(event: MouseEvent) {
    if (!this.selectionSnippet) return;
    stopEvent(event);
  }

  auxclick(event: PointerEvent) {
    if (!isRightClick(event) || !this.selectionSnippet) return;
    stopEvent(event);
    this.openContextMenu(event);
  }

  onClick(event: PointerEvent) {
    if (this.isSameSelection) {
      this.isSameSelection = false;
      stopEvent(event);
      return;
    }
    if (!this.selectionSnippet) return;
    stopEvent(event);
    this.selectionSnippet = '';
    window.getSelection().removeAllRanges();
    this.hubService.changeFocusState(true, false);
  }

  private async openContextMenu(event: { x: number; y: number }) {
    const openAt: { x: number; y: number } = {
      x: event.x,
      y: event?.y,
    };
    const items = await this.buildContextMenuItem();
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: ContextMenuItem) => {
          this.invoke.emit(item.command);
          this.selectionSnippet = '';
        },
      },
      { position: 'below' }
    );
  }

  private buildContextMenuItem(): ContextMenuItem[] {
    return [
      {
        id: 'copy',
        text: 'Copy',
        command: { type: 'copy-clipboard', value: this.selectionSnippet } as Commands.CopyClipboard,
        shortcut: ['CommandOrControl', 'c'] as KeyName[],
        icon: { type: 'font-icon', value: 'icon-copy' },
      },
    ];
  }

  private registerKeyboardHandler() {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    }
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => this.handleKeys(keys, event), 10);
  }

  private handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    if (keys[0] === keyCodes.c.toLowerCase() && keys[1] === keyCodes.commandOrControl.toLowerCase() && this.selectionSnippet) {
      event.stopPropagation();
      return;
    } else if (this.selectionSnippet) {
      stopEvent(event);
    }
  }
}
